import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Divider,
  TextField,
  IconButton,
  Tooltip,
  Alert,
  Chip,
} from "@mui/material";
import {
  ArrowBack,
  Edit,
  SwapHoriz,
  ContentCopy,
  CheckCircle,
  RadioButtonUnchecked,
  DirectionsCar,
  Notes,
  Delete,
  AddPhotoAlternate,
  Close,
} from "@mui/icons-material";
import { useDropzone } from "react-dropzone";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import api from "../../api/axiosInstance";
import PageHeader from "../../components/common/PageHeader";
import VehicleFormDialog from "../../components/vehicles/VehicleFormDialog";
import VehicleStatusDialog from "../../components/vehicles/VehicleStatusDialog";
import { selectStatusChipConfig } from "../../redux/slices/statusDefinitionsSlice";
import { formatUsd } from "../../utils/currency";

dayjs.extend(relativeTime);


const DetailRow = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      py: 0.875,
      borderBottom: "1px solid",
      borderColor: "divider",
      "&:last-child": { borderBottom: "none" },
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={600}
      textAlign="right"
      maxWidth={220}
    >
      {value || "—"}
    </Typography>
  </Box>
);


const VehicleImageThumb = ({ image, vehicleId, onOpen, onDelete, deleting }) => {
  const [thumbUrl, setThumbUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/vehicles/${vehicleId}/images/${image._id}/url`)
      .then(({ data }) => {
        if (!cancelled) setThumbUrl(data.data.url);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [vehicleId, image._id]);

  return (
    <Box
      sx={{
        position: "relative",
        aspectRatio: "1",
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "action.hover",
        cursor: "pointer",
      }}
      onClick={onOpen}
    >
      {loading ? (
        <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <Box
          component="img"
          src={thumbUrl}
          alt={image.originalName || "Vehicle photo"}
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      )}
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        disabled={deleting}
        sx={{
          position: "absolute",
          top: 4,
          right: 4,
          bgcolor: "rgba(0,0,0,0.55)",
          color: "white",
          "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
          width: 24,
          height: 24,
        }}
      >
        {deleting ? <CircularProgress size={12} color="inherit" /> : <Delete sx={{ fontSize: 14 }} />}
      </IconButton>
    </Box>
  );
};

const AdminVehicleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const statusChipConfig = useSelector(selectStatusChipConfig);

  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [internalNote, setInternalNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [copied, setCopied] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUrls, setImageUrls] = useState({}); // { [imageId]: signedUrl }
  const [deletingImageId, setDeletingImageId] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);

  const onDropImage = useCallback(
    async (acceptedFiles) => {
      if (!acceptedFiles.length) return;
      setUploadingImage(true);
      try {
        for (const file of acceptedFiles) {
          const { data: presignData } = await api.post(`/vehicles/${id}/images/presign`, {
            name: file.name,
            type: file.type,
            size: file.size,
          });
          const { key, url, originalName } = presignData.data;

          await axios.put(url, file, {
            headers: { "Content-Type": file.type },
          });

          await api.post(`/vehicles/${id}/images/finalize`, { key, originalName });
        }
        enqueueSnackbar("Image(s) uploaded", { variant: "success" });
        load();
      } catch (err) {
        enqueueSnackbar(err.response?.data?.message || "Failed to upload image", {
          variant: "error",
        });
      } finally {
        setUploadingImage(false);
      }
    },
    [id],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onDropImage,
    accept: { "image/jpeg": [".jpg", ".jpeg"], "image/png": [".png"], "image/webp": [".webp"] },
    maxSize: 10 * 1024 * 1024,
  });

  const loadImageUrl = async (imageId) => {
    if (imageUrls[imageId]) return imageUrls[imageId];
    try {
      const { data } = await api.get(`/vehicles/${id}/images/${imageId}/url`);
      const url = data.data.url;
      setImageUrls((prev) => ({ ...prev, [imageId]: url }));
      return url;
    } catch {
      return null;
    }
  };

  const openLightbox = async (imageId) => {
    const url = await loadImageUrl(imageId);
    if (url) setLightboxImage(url);
  };

  const deleteImage = async (imageId) => {
    setDeletingImageId(imageId);
    try {
      await api.delete(`/vehicles/${id}/images/${imageId}`);
      enqueueSnackbar("Image deleted", { variant: "success" });
      load();
    } catch {
      enqueueSnackbar("Failed to delete image", { variant: "error" });
    } finally {
      setDeletingImageId(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/vehicles/${id}`);
      setVehicle(data.data?.vehicle || null);
      setInternalNote(data.data?.vehicle?.internalNotes || "");
    } catch {
      enqueueSnackbar("Failed to load vehicle shipment", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleEdit = async (payload) => {
    setSaving(true);
    try {
      await api.patch(`/vehicles/${id}`, payload);
      enqueueSnackbar("Vehicle shipment updated", { variant: "success" });
      setEditOpen(false);
      load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to update", {
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleStatusUpdate = async ({ status, label, note, isCustom }) => {
    setUpdatingStatus(true);
    try {
      await api.patch(`/vehicles/${id}/status`, {
        status,
        label,
        note,
        isCustom,
      });
      enqueueSnackbar(`Status updated to: ${label}`, { variant: "success" });
      setStatusOpen(false);
      load();
    } catch (err) {
      enqueueSnackbar(
        err.response?.data?.message || "Failed to update status",
        { variant: "error" },
      );
    } finally {
      setUpdatingStatus(false);
    }
  };

  const saveNote = async () => {
    setSavingNote(true);
    try {
      await api.patch(`/vehicles/${id}`, { internalNotes: internalNote });
      enqueueSnackbar("Note saved", { variant: "success" });
    } catch {
      enqueueSnackbar("Failed to save note", { variant: "error" });
    } finally {
      setSavingNote(false);
    }
  };

  const copyTrackingLink = () => {
    const link = `${window.location.origin}/track/${vehicle.trackingId}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading && !vehicle) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!vehicle) return null;

  const statusCfg = statusChipConfig[vehicle.status] || {
    bg: "#EDF2F7",
    color: "#4A5568",
  };
  const sortedHistory = [...(vehicle.statusHistory || [])].sort(
    (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Button
          startIcon={<ArrowBack />}
          variant="outlined"
          size="small"
          onClick={() => navigate("/admin/vehicles")}
        >
          Back
        </Button>
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
              mb: 0.25,
            }}
          >
            <Typography variant="h4" fontWeight={700}>
              {vehicle.vehicle?.year} {vehicle.vehicle?.make}{" "}
              {vehicle.vehicle?.model}
            </Typography>
            <Chip
              label={vehicle.statusLabel || vehicle.status}
              size="small"
              sx={{
                bgcolor: statusCfg.bg,
                color: statusCfg.color,
                fontWeight: 700,
                fontSize: 12,
              }}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="caption"
              sx={{
                fontFamily: "monospace",
                color: "primary.main",
                fontWeight: 700,
              }}
            >
              {vehicle.trackingId}
            </Typography>
            <Tooltip title={copied ? "Copied!" : "Copy tracking link"}>
              <IconButton size="small" onClick={copyTrackingLink}>
                {copied ? (
                  <CheckCircle sx={{ fontSize: 14, color: "success.main" }} />
                ) : (
                  <ContentCopy sx={{ fontSize: 14 }} />
                )}
              </IconButton>
            </Tooltip>
            {!vehicle.isPublic && (
              <Chip
                label="Private"
                size="small"
                sx={{
                  fontSize: 10,
                  height: 18,
                  bgcolor: "action.selected",
                  color: "text.secondary",
                }}
              />
            )}
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Edit />}
            onClick={() => setEditOpen(true)}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<SwapHoriz />}
            onClick={() => setStatusOpen(true)}
          >
            Update Status
          </Button>
        </Box>
      </Box>

      {/* Public tracking link alert */}
      {vehicle.isPublic && (
        <Alert
          severity="info"
          sx={{ borderRadius: 2 }}
          action={
            <Button size="small" color="info" onClick={copyTrackingLink}>
              {copied ? "Copied!" : "Copy Link"}
            </Button>
          }
        >
          Public tracking link:{" "}
          <strong>
            {window.location.origin}/track/{vehicle.trackingId}
          </strong>
        </Alert>
      )}

      {/* Main grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 320px" },
          gap: 2.5,
        }}
      >
        {/* Left column */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* Vehicle + Customer details */}
          <Card>
            <CardHeader
              title={
                <Typography fontWeight={700} fontSize={14}>
                  Details
                </Typography>
              }
              sx={{ pb: 0 }}
            />
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 }}
            >
              <Box
                sx={{
                  p: 2.5,
                  borderRight: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="overline"
                  color="text.secondary"
                  display="block"
                  mb={1.5}
                >
                  Vehicle
                </Typography>
                <DetailRow label="Make" value={vehicle.vehicle?.make} />
                <DetailRow label="Model" value={vehicle.vehicle?.model} />
                <DetailRow label="Year" value={vehicle.vehicle?.year} />
                <DetailRow label="Color" value={vehicle.vehicle?.color} />
                <DetailRow
                  label="Engine No."
                  value={vehicle.vehicle?.engineNo}
                />
                <DetailRow
                  label="Chassis No."
                  value={vehicle.vehicle?.chassisNo}
                />
                <DetailRow
                  label="Declared Value"
                  value={
                    vehicle.declaredValue ? formatUsd(vehicle.declaredValue) : null
                  }
                />
              </Box>
              <Box sx={{ p: 2.5 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  display="block"
                  mb={1.5}
                >
                  Customer
                </Typography>
                <DetailRow label="Name" value={vehicle.customerName} />
                <DetailRow label="Phone" value={vehicle.customerPhone} />
                <DetailRow label="Email" value={vehicle.customerEmail} />

                <Divider sx={{ my: 2 }} />
                <Typography
                  variant="overline"
                  color="text.secondary"
                  display="block"
                  mb={1.5}
                >
                  Origin
                </Typography>
                <DetailRow label="Country" value={vehicle.origin?.country} />
                <DetailRow label="Port" value={vehicle.origin?.port} />
                <DetailRow label="Dealer" value={vehicle.origin?.dealerName} />
              </Box>
            </Box>
          </Card>

          {/* Shipping details */}
          <Card>
            <CardHeader
              title={
                <Typography fontWeight={700} fontSize={14}>
                  Shipping
                </Typography>
              }
              sx={{ pb: 0 }}
            />
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 }}
            >
              <Box
                sx={{
                  p: 2.5,
                  borderRight: "1px solid",
                  borderColor: "divider",
                }}
              >
                <DetailRow
                  label="Vessel"
                  value={vehicle.shipping?.vesselName}
                />
                <DetailRow
                  label="Bill of Lading"
                  value={vehicle.shipping?.billOfLading}
                />
                <DetailRow
                  label="Container #"
                  value={vehicle.shipping?.containerNumber}
                />
                <DetailRow
                  label="Booking Ref"
                  value={vehicle.shipping?.bookingReference}
                />
              </Box>
              <Box sx={{ p: 2.5 }}>
                <DetailRow
                  label="Departure Date"
                  value={
                    vehicle.shipping?.departureDate
                      ? dayjs(vehicle.shipping.departureDate).format(
                        "MMM D, YYYY",
                      )
                      : null
                  }
                />
                <DetailRow
                  label="Est. Arrival"
                  value={
                    vehicle.shipping?.estimatedArrivalDate
                      ? dayjs(vehicle.shipping.estimatedArrivalDate).format(
                        "MMM D, YYYY",
                      )
                      : null
                  }
                />
                <DetailRow
                  label="Actual Arrival"
                  value={
                    vehicle.shipping?.actualArrivalDate
                      ? dayjs(vehicle.shipping.actualArrivalDate).format(
                        "MMM D, YYYY",
                      )
                      : null
                  }
                />
                <DetailRow
                  label="Destination"
                  value={vehicle.destination?.port}
                />
              </Box>
            </Box>
          </Card>

          {/* Vehicle Photos */}
          <Card>
            <CardHeader
              title={
                <Typography fontWeight={700} fontSize={14}>
                  Vehicle Photos ({vehicle.images?.length || 0})
                </Typography>
              }
              sx={{ pb: 0 }}
            />
            <CardContent>
              <Box
                {...getRootProps()}
                sx={{
                  border: "2px dashed",
                  borderColor: isDragActive ? "primary.main" : "divider",
                  borderRadius: 2,
                  p: 2.5,
                  mb: vehicle.images?.length > 0 ? 2 : 0,
                  textAlign: "center",
                  cursor: "pointer",
                  bgcolor: isDragActive ? "rgba(13,98,155,0.04)" : "action.hover",
                }}
              >
                <input {...getInputProps()} />
                <AddPhotoAlternate sx={{ fontSize: 32, color: "text.disabled", mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {uploadingImage
                    ? "Uploading…"
                    : "Drag & drop photos here, or click to browse (JPG, PNG, WEBP, max 10MB)"}
                </Typography>
              </Box>

              {vehicle.images?.length > 0 && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(2, 1fr)",
                      sm: "repeat(3, 1fr)",
                      md: "repeat(4, 1fr)",
                    },
                    gap: 1.5,
                  }}
                >
                  {vehicle.images.map((img) => (
                    <VehicleImageThumb
                      key={img._id}
                      image={img}
                      vehicleId={id}
                      onOpen={() => openLightbox(img._id)}
                      onDelete={() => deleteImage(img._id)}
                      deleting={deletingImageId === img._id}
                    />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>


          {/* Status Timeline */}
          <Card>
            <CardHeader
              title={
                <Typography fontWeight={700} fontSize={14}>
                  Status History
                </Typography>
              }
              sx={{ pb: 0 }}
            />
            <Box sx={{ px: 2.5, py: 2 }}>
              {sortedHistory.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No status history yet.
                </Typography>
              ) : (
                sortedHistory.map((entry, idx) => (
                  <Box
                    key={idx}
                    sx={{ display: "flex", gap: 1.5, position: "relative" }}
                  >
                    {idx < sortedHistory.length - 1 && (
                      <Box
                        sx={{
                          position: "absolute",
                          left: 11,
                          top: 26,
                          width: 2,
                          height: "calc(100% - 10px)",
                          bgcolor: idx === 0 ? "primary.main" : "divider",
                        }}
                      />
                    )}
                    <Box sx={{ flexShrink: 0, mt: 0.5, zIndex: 1 }}>
                      {idx === 0 ? (
                        <CheckCircle
                          sx={{ fontSize: 24, color: "primary.main" }}
                        />
                      ) : (
                        <RadioButtonUnchecked
                          sx={{ fontSize: 24, color: "text.disabled" }}
                        />
                      )}
                    </Box>
                    <Box sx={{ pb: 2.5, flex: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: idx === 0 ? "primary.main" : "text.primary",
                          }}
                        >
                          {entry.label || entry.status}
                        </Typography>
                        {entry.isCustom && (
                          <Chip
                            label="Custom"
                            size="small"
                            sx={{
                              fontSize: 9,
                              height: 16,
                              // Translucent purple instead of a solid pastel —
                              // a mid-lightness purple reads fine on both a
                              // light and a dark surface, so this needs no
                              // mode-specific branching.
                              bgcolor: "rgba(147,51,234,0.14)",
                              color: "#9333EA",
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Box>
                      {entry.note && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mt={0.25}
                        >
                          {entry.note}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        display="block"
                        mt={0.5}
                      >
                        {dayjs(entry.timestamp).format(
                          "MMM D, YYYY [at] h:mm A",
                        )}
                        {entry.updatedBy &&
                          ` · ${entry.updatedBy.firstName} ${entry.updatedBy.lastName}`}
                      </Typography>
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Card>
        </Box>

        {/* Right sidebar */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Internal notes */}
          <Card>
            <CardHeader
              title={
                <Typography fontWeight={700} fontSize={13}>
                  Internal Notes
                </Typography>
              }
              avatar={<Notes sx={{ color: "text.secondary", fontSize: 18 }} />}
              sx={{ pb: 1 }}
            />
            <CardContent sx={{ pt: 0 }}>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Add internal notes visible only to staff…"
                value={internalNote}
                onChange={(e) => setInternalNote(e.target.value)}
                size="small"
                sx={{ mb: 1.5 }}
              />
              <Button
                size="small"
                variant="contained"
                fullWidth
                onClick={saveNote}
                disabled={savingNote}
              >
                {savingNote ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  "Save Note"
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Quick info */}
          <Card>
            <CardHeader
              title={
                <Typography fontWeight={700} fontSize={13}>
                  Quick Info
                </Typography>
              }
              sx={{ pb: 1 }}
            />
            <CardContent sx={{ pt: 0 }}>
              <DetailRow
                label="Created"
                value={dayjs(vehicle.createdAt).format("MMM D, YYYY")}
              />
              <DetailRow
                label="Last Updated"
                value={dayjs(vehicle.updatedAt).fromNow()}
              />
              <DetailRow
                label="Total Updates"
                value={`${vehicle.statusHistory?.length || 0} status events`}
              />
              <DetailRow
                label="Public Tracking"
                value={vehicle.isPublic ? "✅ Enabled" : "❌ Disabled"}
              />
              {vehicle.isPublic && (
                <Box
                  sx={{
                    mt: 1.5,
                    p: 1.5,
                    bgcolor: "action.hover",
                    borderRadius: 1.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    mb={0.5}
                  >
                    Customer tracking link
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: "monospace",
                      wordBreak: "break-all",
                      color: "primary.main",
                    }}
                  >
                    /track/{vehicle.trackingId}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {lightboxImage && (
        <Box
          onClick={() => setLightboxImage(null)}
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1400,
            cursor: "pointer",
          }}
        >
          <IconButton
            onClick={() => setLightboxImage(null)}
            sx={{ position: "absolute", top: 16, right: 16, color: "white" }}
          >
            <Close />
          </IconButton>
          <Box
            component="img"
            src={lightboxImage}
            sx={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain" }}
          />
        </Box>
      )}

      {/* Dialogs */}
      <VehicleFormDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSubmit={handleEdit}
        initial={vehicle}
        loading={saving}
      />

      <VehicleStatusDialog
        open={statusOpen}
        onClose={() => setStatusOpen(false)}
        onSubmit={handleStatusUpdate}
        vehicle={vehicle}
        loading={updatingStatus}
      />
    </Box>
  );
};

export default AdminVehicleDetail;
